import { InfoOutlined as InfoOutlinedIcon, PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import CancelIcon from 'assets/images/icons/CancelIcon.svg?react';
import EmployeeName from 'components/employee/EmployeeName';
import VacationMarker from 'components/employee/VacationMarker';
import LazyTooltip from 'components/LazyTooltip';
import MatterIndicators from 'components/matters/MatterIndicators';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal.tsx';
import { useModal } from 'components/modals/useModal';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useInvoice } from 'pages/invoices/hooks/useInvoice.ts';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useDeleteInvoiceMatterMutation, useUpdateInvoiceMatterMutation } from 'store/api/invoice-details';
import { ClassifierData } from 'types/classifiers';
import { EntityType } from 'types/entity.ts';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterNameDetailsForm } from 'types/invoice-details';
import { InvoiceData, InvoiceMatterDetailsDto, InvoiceStatusType } from 'types/invoices';
import { PricingLimitationType } from 'types/matters.ts';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { displayName, NamedEntityParts, useLastNameFirst } from 'utils/nameOrder';
import { partyNameString } from 'utils/partyNameUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { ItemActionButton } from '../../components/ItemActionButton';
import { ItemEditButton } from '../InvoiceItemRow';
import { SECTION_TITLE } from '../SectionCard';
import InvoiceMatterNameEditModal from './InvoiceMatterNameEditModal';

interface Props {
    invoice: InvoiceData;
    isInViewMode: boolean;
    invoiceMatter: InvoiceMatterDetailsDto;
    isLocalizedFieldsEnabled: boolean;
    isLoading?: boolean;
    classifiers: Record<string, ClassifierData>;
}

const STATUSES_ALLOWING_CASE_DELETING: string[] = [
    InvoiceStatusType.DRAFT,
    InvoiceStatusType.REVIEWED,
    InvoiceStatusType.APPROVED,
    InvoiceStatusType.FINALIZED,
    InvoiceStatusType.POSTPONED,
];

/** A case may leave an invoice that keeps another, is neither credited nor split, and is not yet sent. */
export const useIsMatterRemovable = (invoice: InvoiceData) => {
    const { isCredit, isPartiallyCredited } = useInvoice(invoice);
    return (
        !isCredit &&
        !isPartiallyCredited &&
        !invoice.hasCreditInvoice &&
        !(invoice.isSplit ?? false) &&
        invoice.invoiceMatters.length > 1 &&
        STATUSES_ALLOWING_CASE_DELETING.includes(invoice.invoiceStatusTypeCode)
    );
};

/** The case's names on this invoice, as their form opens: the specification's fall back to the invoice's. */
export const caseNameValues = (invoiceMatter: InvoiceMatterDetailsDto): InvoiceMatterNameDetailsForm => ({
    matterId: invoiceMatter.matterId,
    invoiceMatterName: invoiceMatter.invoiceMatterName || '',
    invoiceMatterNameLocalLanguage: invoiceMatter.invoiceMatterNameLocalLanguage || '',
    specificationInvoiceMatterName:
        invoiceMatter.specificationInvoiceMatterName || invoiceMatter.invoiceMatterName || '',
    specificationInvoiceMatterNameLocalLanguage:
        invoiceMatter.specificationInvoiceMatterNameLocalLanguage || invoiceMatter.invoiceMatterNameLocalLanguage || '',
});

const InvoiceCardRow = ({ titleId, fieldValue, rowKey }: { titleId: string; fieldValue?: string; rowKey: string }) => (
    <React.Fragment key={rowKey}>
        <p className="text-fg-secondary mt-[5px] text-[0.8rem] leading-[1.375rem]">{t(titleId)}</p>
        <p className="text-body1 text-primary">{fieldValue || '-'}</p>
    </React.Fragment>
);

const SupervisionItem = ({
    labelKey,
    entity,
    entityType,
}: {
    labelKey: string;
    entity: NamedEntityParts & { id: number };
    entityType: EntityType;
}) => {
    const redirectUrl =
        entityType == EntityType.EMPLOYEE
            ? `/employee/${entity.id}`
            : entityType == EntityType.GENERIC_PARTY
              ? `/contact/${entity.id}`
              : '/';
    const isNewDesign = useIsNewDesign();
    const lastNameFirst = useLastNameFirst();
    return (
        <div className="flex gap-1 text-[13px] font-normal">
            <div className="text-fg">{t(labelKey)}</div>
            {isNewDesign ? <VacationMarker onVacation={entity.onVacation} /> : null}
            <RecordLink
                to={redirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-fg hover:text-link w-fit no-underline hover:underline"
                label={displayName({ ...entity, onVacation: false }, lastNameFirst)}
            >
                <span className="text-fg-secondary">
                    <EmployeeName entity={entity} />
                </span>
            </RecordLink>
        </div>
    );
};

const MatterNameOnInvocieCard = ({
    invoiceMatter,
    isLocalizedFieldsEnabled,
}: {
    invoiceMatter: InvoiceMatterDetailsDto;
    isLocalizedFieldsEnabled: boolean;
}) => (
    <div className="p-2">
        <InvoiceCardRow
            titleId="invoices.details.case-name-on-invoice"
            fieldValue={invoiceMatter.invoiceMatterName}
            rowKey={`${invoiceMatter.matterId}-imn`}
        />
        {isLocalizedFieldsEnabled ? (
            <InvoiceCardRow
                titleId="invoices.details.case-name-on-invoice-local-lang"
                fieldValue={invoiceMatter.invoiceMatterNameLocalLanguage}
                rowKey={`${invoiceMatter.matterId}-imnll`}
            />
        ) : null}
        <InvoiceCardRow
            titleId="invoices.details.case-name-on-spec"
            fieldValue={invoiceMatter.specificationInvoiceMatterName}
            rowKey={`${invoiceMatter.matterId}-simn`}
        />
        {isLocalizedFieldsEnabled ? (
            <InvoiceCardRow
                titleId="invoices.details.case-name-on-spec-local-lang"
                fieldValue={invoiceMatter.specificationInvoiceMatterNameLocalLanguage}
                rowKey={`${invoiceMatter.matterId}-simnll`}
            />
        ) : null}
    </div>
);

export const InvoiceMatterNameDetails = ({
    invoice,
    classifiers,
    isLoading = false,
    isInViewMode,
    invoiceMatter,
    isLocalizedFieldsEnabled,
}: Props): JSX.Element => {
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const lastNameFirst = useLastNameFirst();
    const { openModal, closeModal } = useModal();
    const isSplitInvoice = invoice.isSplit ?? false;
    const isMatterRemovable = useIsMatterRemovable(invoice);
    const isTooltipEnabled =
        !!invoiceMatter.invoiceMatterName ||
        !!invoiceMatter.specificationInvoiceMatterName ||
        (isLocalizedFieldsEnabled &&
            (!!invoiceMatter.invoiceMatterNameLocalLanguage ||
                !!invoiceMatter.specificationInvoiceMatterNameLocalLanguage));

    const alertIcon = (
        <span className="bg-error-light mr-4 flex size-6 items-center justify-center rounded-[4px]">
            <PriorityHighRoundedIcon className="text-error text-[14px]" />
        </span>
    );

    const [updateInvoiceMatter] = useUpdateInvoiceMatterMutation();
    const [deleteInvoiceMatter] = useDeleteInvoiceMatterMutation();

    const initialValues = caseNameValues(invoiceMatter);

    const { handleSubmit, control } = useForm<InvoiceMatterNameDetailsForm>({
        defaultValues: initialValues,
        values: initialValues,
    });

    const cancelEditing = useCallback(() => {
        closeModal();
    }, [closeModal]);

    const openInvoiceMatterNameEditModal = useCallback((): void => {
        openModal(
            <InvoiceMatterNameEditModal
                onSubmit={() => {
                    void handleSubmit((form) => {
                        try {
                            const data = {
                                invoiceId: invoice.id,
                                matterId: form.matterId,
                                invoiceMatterName: form.invoiceMatterName,
                                invoiceMatterNameLocalLanguage: form.invoiceMatterNameLocalLanguage,
                                specificationInvoiceMatterName: form.specificationInvoiceMatterName,
                                specificationInvoiceMatterNameLocalLanguage:
                                    form.specificationInvoiceMatterNameLocalLanguage,
                            };
                            updateInvoiceMatter({ ...data })
                                .unwrap()
                                .catch(console.error);
                        } catch (e) {
                            console.error('onSubmit error: ', e);
                            errorAlert(t('forms.error-submitting-the-form'));
                        }
                    })();
                }}
                onClose={cancelEditing}
                control={control}
            />,
            cancelEditing
        );
    }, [cancelEditing, control, handleSubmit, invoice.id, openModal, updateInvoiceMatter]);

    const editButton = <ItemEditButton onClick={openInvoiceMatterNameEditModal} disabled={buttonsDisabled} />;

    const deleteButton = (
        <ItemActionButton
            tooltip={t('invoices.tooltips.delete-case')}
            disabled={buttonsDisabled}
            onClick={() =>
                openModal(
                    <ConfirmationModal
                        onCancel={closeModal}
                        onConfirm={() => {
                            void deleteInvoiceMatter({
                                invoiceId: invoice.id,
                                matterId: invoiceMatter.matterId,
                            })
                                .unwrap()
                                .then(() => closeModal())
                                .catch(console.error);
                        }}
                        content={<p className="text-body1">{t('invoices.alerts.delete-case-confirmation-text')}</p>}
                    />
                )
            }
        >
            <CancelIcon className="size-[18px]" />
        </ItemActionButton>
    );

    const isFixedFeeArrangement =
        invoiceMatter.feeArrangementTypeCode === PricingLimitationType.FIXED_ONETIME ||
        invoiceMatter.feeArrangementTypeCode === PricingLimitationType.FIXED_RECURRING;

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                {/* Once per case per view: the rows grouped beneath this header do not repeat it. */}
                <MatterIndicators isConfidential={invoiceMatter.isConfidential} />
                <p className={cn(SECTION_TITLE, 'cursor-text select-text')} onClick={(e) => e.stopPropagation()}>
                    <FormattedMessage id="invoices.details.case" values={{ fullName: invoiceMatter.fullName ?? '' }} />
                </p>
                {isTooltipEnabled ? (
                    <LazyTooltip
                        placement="bottom"
                        contentClassName="w-[400px] max-w-[400px] rounded-[7px] text-[11px] new:text-caption"
                        title={
                            <MatterNameOnInvocieCard
                                invoiceMatter={invoiceMatter}
                                isLocalizedFieldsEnabled={isLocalizedFieldsEnabled}
                            />
                        }
                    >
                        <InfoOutlinedIcon className="text-grey-600 h-[1.2rem]" />
                    </LazyTooltip>
                ) : null}
                {!isInViewMode ? editButton : null}
                {isSplitInvoice ? (
                    <FormattedMessage id="invoices.split-invoice" />
                ) : (
                    getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], invoiceMatter?.feeArrangementTypeCode)
                )}
                {!isInViewMode && isMatterRemovable ? deleteButton : null}
                {isFixedFeeArrangement && !isSplitInvoice && invoiceMatter.fixedFee
                    ? ` ${invoiceMatter.fixedFee} ${invoiceMatter.pricelistCurrencyTypeCode}`
                    : null}
                {invoiceMatter.hasMatterCapError && !isSplitInvoice ? alertIcon : null}
                {isLoading ? (
                    <span
                        role="progressbar"
                        aria-label={t('loading')}
                        className="border-primary ml-10 size-5 animate-spin rounded-full border-2 border-t-transparent"
                    />
                ) : null}
            </div>
            <div className="flex gap-4">
                {invoiceMatter.responsibleEmployee && (
                    <SupervisionItem
                        labelKey="invoices.responsible-lawyer"
                        entity={invoiceMatter.responsibleEmployee}
                        entityType={EntityType.EMPLOYEE}
                    />
                )}
                {invoiceMatter.supervisedByEmployee && (
                    <SupervisionItem
                        labelKey="invoices.supervised-by"
                        entity={invoiceMatter.supervisedByEmployee}
                        entityType={EntityType.EMPLOYEE}
                    />
                )}
                <SupervisionItem
                    labelKey="invoices.client"
                    entity={{
                        id: invoiceMatter.clientParty.id,
                        name: partyNameString(invoiceMatter.clientParty, {
                            showPartyNumber: true,
                            isPartyNumberUsageEnabled: isPartyNumberUsageEnabled,
                            lastNameFirst,
                        }),
                    }}
                    entityType={EntityType.GENERIC_PARTY}
                />
            </div>
        </div>
    );
};
